import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { KeyRound, Loader2, UserCog, Smartphone, Mail } from "lucide-react";
import { api } from "../lib/api";
import { RecoveryEmailSection } from "./RecoveryEmailSection";
import { useAuth } from "../context/AuthContext";

export const ChangePasswordDialog = ({ open, onOpenChange, portal = false }) => {
  const { user, checkAuth } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  // login credentials (phone/email) — company accounts only, not the employee portal
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [credPass, setCredPass] = useState("");
  const [credBusy, setCredBusy] = useState(false);
  // owner identity-based change (CR number + last 4 of login phone) when old password is forgotten
  const [idMode, setIdMode] = useState(false);
  const [crNum, setCrNum] = useState("");
  const [last4, setLast4] = useState("");
  const [idVerified, setIdVerified] = useState(false);
  const [idBusy, setIdBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setCur(""); setNw(""); setConfirm(""); setCredPass("");
      setIdMode(false); setCrNum(""); setLast4(""); setIdVerified(false);
      setPhone(user?.phone || "");
      setEmail((user?.email || "").endsWith("@phone.local") ? "" : (user?.email || ""));
    }
  }, [open, user]);

  const hasPassword = portal ? true : !!user?.has_password;

  const submit = async () => {
    if ((hasPassword && !cur) || !nw) { toast.warning(hasPassword ? "أدخل كلمة المرور الحالية والجديدة" : "أدخل كلمة المرور الجديدة"); return; }
    if (!/^\d{6}$/.test(nw)) { toast.warning("الرمز السري يجب أن يكون 6 أرقام بالضبط"); return; }
    if (nw !== confirm) { toast.warning("تأكيد كلمة المرور غير مطابق"); return; }
    setBusy(true);
    try {
      if (portal) await api.portalChangePassword(cur, nw);
      else await api.changeOwnPassword(cur, nw);
      toast.success(hasPassword ? "تم تغيير كلمة المرور بنجاح" : "تم تعيين كلمة المرور بنجاح");
      await checkAuth?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تغيير كلمة المرور");
    } finally { setBusy(false); }
  };

  const verifyIdentity = async () => {
    if (!crNum.trim() || last4.trim().length !== 4) { toast.warning("أدخل رقم السجل التجاري وآخر 4 أرقام من الجوال"); return; }
    setIdBusy(true);
    try {
      await api.verifyOwnerIdentity(crNum.trim(), last4.trim());
      setIdVerified(true);
      toast.success("تم التحقق — يمكنك الآن تعيين كلمة مرور جديدة");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر التحقق");
    } finally { setIdBusy(false); }
  };

  const submitIdentity = async () => {
    if (!nw) { toast.warning("أدخل كلمة المرور الجديدة"); return; }
    if (!/^\d{6}$/.test(nw)) { toast.warning("الرمز السري يجب أن يكون 6 أرقام بالضبط"); return; }
    if (nw !== confirm) { toast.warning("تأكيد كلمة المرور غير مطابق"); return; }
    setBusy(true);
    try {
      await api.changePasswordIdentity(crNum.trim(), last4.trim(), nw);
      toast.success("تم تغيير كلمة المرور بنجاح");
      await checkAuth?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تغيير كلمة المرور");
    } finally { setBusy(false); }
  };

  const saveCreds = async () => {
    const newPhone = phone.trim();
    const newEmail = email.trim();
    if (!newPhone && !newEmail) { toast.warning("أدخل رقم الجوال أو البريد الإلكتروني"); return; }
    const changed = {};
    if (newPhone && newPhone !== (user?.phone || "")) changed.new_phone = newPhone;
    if (newEmail && newEmail.toLowerCase() !== (user?.email || "").toLowerCase()) changed.new_email = newEmail;
    if (!changed.new_phone && !changed.new_email) { toast.info("لا يوجد تغيير في بيانات الدخول"); return; }
    if (hasPassword && !credPass) { toast.warning("أدخل كلمة المرور الحالية للتأكيد"); return; }
    if (hasPassword) changed.current_password = credPass;
    setCredBusy(true);
    try {
      await api.updateCredentials(changed);
      await checkAuth();
      setCredPass("");
      toast.success("تم تحديث بيانات الدخول بنجاح");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "تعذّر تحديث بيانات الدخول");
    } finally { setCredBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm font-body max-h-[90vh] overflow-y-auto" dir="rtl" data-testid="change-password-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <UserCog className="h-5 w-5 text-blue-600" /> بيانات الدخول والأمان
          </DialogTitle>
          <DialogDescription className="text-right">{portal ? "غيّر رمزك السري (6 أرقام)." : "حدّث رقم جوالك وبريدك ورمزك السري (6 أرقام)."}</DialogDescription>
        </DialogHeader>

        {!portal && (
          <div className="space-y-3 rounded-xl border border-slate-200 p-3" data-testid="login-credentials-section">
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <Smartphone className="h-4 w-4 text-emerald-600" /> بيانات الدخول (الجوال/البريد)
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="cred-phone">رقم الجوال للدخول</Label>
              <Input id="cred-phone" dir="ltr" inputMode="tel" placeholder="05xxxxxxxx" data-testid="cred-phone-input"
                value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cred-email" className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> البريد الإلكتروني للدخول</Label>
              <Input id="cred-email" dir="ltr" type="email" placeholder="name@example.com" data-testid="cred-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            {hasPassword && (
              <div className="space-y-1.5">
                <Label htmlFor="cred-pass">كلمة المرور الحالية (للتأكيد)</Label>
                <Input id="cred-pass" type="password" data-testid="cred-current-password-input"
                  value={credPass} onChange={(e) => setCredPass(e.target.value)} />
              </div>
            )}
            <Button data-testid="submit-update-credentials" disabled={credBusy} onClick={saveCreds}
              className="w-full bg-emerald-600 hover:bg-emerald-700 gap-1.5">
              {credBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCog className="h-4 w-4" />} حفظ بيانات الدخول
            </Button>
          </div>
        )}
        {!portal && <RecoveryEmailSection open={open} hasPassword={hasPassword} />}

        <div className="space-y-3 rounded-xl border border-slate-200 p-3">
          <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
            <KeyRound className="h-4 w-4 text-blue-600" /> {hasPassword ? "تغيير كلمة المرور" : "تعيين كلمة المرور"}
          </p>
          {!hasPassword && (
            <p className="text-xs text-slate-600 bg-blue-50 border border-blue-100 rounded-lg p-2">
              حسابك مسجّل عبر Google، لذا لا توجد كلمة مرور قديمة. عيّن كلمة مرور الآن لتتمكن أيضاً من الدخول بالبريد وكلمة المرور.
            </p>
          )}

          {!portal && hasPassword && idMode ? (
            <div className="space-y-3" data-testid="identity-verify-section">
              <p className="text-xs text-slate-600 bg-amber-50 border border-amber-100 rounded-lg p-2">
                نسيت كلمة المرور الحالية؟ تحقّق من هويتك بإدخال رقم السجل التجاري وآخر 4 أرقام من جوال الدخول المسجّل، ثم عيّن كلمة مرور جديدة.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="cr-number">رقم السجل التجاري</Label>
                <Input id="cr-number" dir="ltr" inputMode="numeric" placeholder="10xxxxxxxx" data-testid="cr-number-input"
                  disabled={idVerified} value={crNum} onChange={(e) => setCrNum(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="phone-last4">آخر 4 أرقام من جوال الدخول</Label>
                <Input id="phone-last4" dir="ltr" inputMode="numeric" maxLength={4} placeholder="••••" data-testid="phone-last4-input"
                  disabled={idVerified} value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} />
              </div>
              {!idVerified ? (
                <Button data-testid="verify-identity-btn" disabled={idBusy} onClick={verifyIdentity} className="w-full bg-amber-600 hover:bg-amber-700 gap-1.5">
                  {idBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} تحقّق
                </Button>
              ) : (
                <>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-pass-id">كلمة المرور الجديدة</Label>
                    <Input id="new-pass-id" pin data-testid="identity-new-password-input" value={nw} onChange={(e) => setNw(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-pass-id">تأكيد كلمة المرور</Label>
                    <Input id="confirm-pass-id" pin data-testid="identity-confirm-password-input" value={confirm}
                      onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submitIdentity()} />
                  </div>
                  <Button data-testid="submit-change-password-identity" disabled={busy} onClick={submitIdentity} className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} حفظ كلمة المرور
                  </Button>
                </>
              )}
              <button type="button" data-testid="use-current-password-link" onClick={() => { setIdMode(false); setIdVerified(false); setNw(""); setConfirm(""); }}
                className="w-full text-xs text-slate-500 hover:text-blue-600 underline">
                الرجوع واستخدام كلمة المرور الحالية
              </button>
            </div>
          ) : (
            <>
              {hasPassword && (
                <div className="space-y-1.5">
                  <Label htmlFor="cur-pass">كلمة المرور الحالية</Label>
                  <Input id="cur-pass" type="password" data-testid="current-password-input" value={cur} onChange={(e) => setCur(e.target.value)} />
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="new-pass">كلمة المرور الجديدة</Label>
                <Input id="new-pass" pin data-testid="new-password-input" value={nw} onChange={(e) => setNw(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pass">تأكيد كلمة المرور</Label>
                <Input id="confirm-pass" pin data-testid="confirm-password-input" value={confirm}
                  onChange={(e) => setConfirm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} />
              </div>
              <Button data-testid="submit-change-password" disabled={busy} onClick={submit} className="w-full bg-blue-600 hover:bg-blue-700 gap-1.5">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} {hasPassword ? "حفظ كلمة المرور" : "تعيين كلمة المرور"}
              </Button>
              {!portal && hasPassword && (
                <button type="button" data-testid="forgot-current-password-link" onClick={() => { setIdMode(true); setCur(""); setNw(""); setConfirm(""); }}
                  className="w-full text-xs text-amber-600 hover:text-amber-700 underline">
                  نسيت كلمة المرور الحالية؟ تحقّق عبر السجل التجاري
                </button>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
